import { useState } from 'react';
import { confirmDialog } from '../../utils/dialogs';
import { API_BASE } from '../../lib/api';

const getStoredToken = () => {
  if (typeof localStorage === 'undefined') return '';
  return (
    localStorage.getItem('accessToken') ||
    localStorage.getItem('go2cdl_access_token') ||
    ''
  );
};

export default function TruckManager({ trucks, selectedTruckId, onChange, variant = 'toolbar' }) {
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editorState, setEditorState] = useState({
    open: false,
    mode: null,
    name: '',
    type: 'SKILL',
    truckId: null,
  });

  const findActiveTruck = () => {
    if (!trucks?.length) return null;
    if (!selectedTruckId) return trucks[0];
    return trucks.find(t => t.id === selectedTruckId) || trucks[0];
  };

  const openEditor = (mode) => {
    if (busy) return;
    if (mode === 'add') {
      setEditorState({ open: true, mode, name: '', type: 'SKILL', truckId: null });
      return;
    }

    const truck = findActiveTruck();
    if (!truck) {
      alert('No trucks available to modify.');
      return;
    }

    setEditorState({
      open: true,
      mode,
      name: truck.name || '',
      type: (truck.trainingType || 'SKILL').toUpperCase(),
      truckId: truck.id,
    });
  };

  const closeEditor = () => setEditorState(prev => ({ ...prev, open: false }));

  const submitEditor = async (event) => {
    event.preventDefault();
    if (busy) return;
    const trimmedName = editorState.name.trim();
    if (!trimmedName) {
      alert('Truck name is required.');
      return;
    }
    setBusy(true);
    let wasSuccessful = false;
    try {
      if (editorState.mode === 'add') {
        const res = await fetch(`${API_BASE}/api/admin/trucks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStoredToken()}`
          },
          body: JSON.stringify({ name: trimmedName, trainingType: editorState.type })
        });
        if (res.ok) {
          const created = await res.json();
          const normalizedType = editorState.type;
          created.trainingType = normalizedType;
          created.trainingLabel = normalizedType === 'ROAD' ? 'Road' : 'Skills';
          const next = [...(trucks || []), created].sort((a, b) => a.name.localeCompare(b.name));
          onChange(next, created.id);
          wasSuccessful = true;
        } else {
          const txt = await res.text();
          alert(`Failed to add truck: ${txt || res.status}`);
        }
      } else if (editorState.mode === 'edit' && editorState.truckId) {
        const res = await fetch(`${API_BASE}/api/admin/trucks/${editorState.truckId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStoredToken()}`
          },
          body: JSON.stringify({
            name: trimmedName,
            trainingType: editorState.type,
          })
        });
        if (res.ok) {
          const updated = await res.json();
          const normalizedType = editorState.type;
          updated.trainingType = normalizedType;
          updated.trainingLabel = normalizedType === 'ROAD' ? 'Road' : 'Skills';
          const next = (trucks || []).map(t => t.id === updated.id ? updated : t).sort((a, b) => a.name.localeCompare(b.name));
          onChange(next, updated.id);
          wasSuccessful = true;
        } else {
          const txt = await res.text();
          alert(`Failed to modify truck: ${txt || res.status}`);
        }
      }
    } catch (error) {
      alert(`Request failed: ${error.message}`);
    } finally {
      setBusy(false);
      if (wasSuccessful) {
        closeEditor();
      }
    }
  };

  const deleteTruck = async () => {
    if (busy) return;
    const truck = findActiveTruck();
    if (!truck) {
      alert('No trucks available to delete.');
      return;
    }
    if (!confirmDialog(`Delete ${truck.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/trucks/${truck.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getStoredToken()}` }
      });
      if (res.ok) {
        const next = (trucks || []).filter(t => t.id !== truck.id).sort((a, b) => a.name.localeCompare(b.name));
        onChange(next, next.length ? next[0].id : null);
      } else {
        const rawBody = await res.text();
        let message = rawBody || res.statusText || String(res.status);
        try {
          const parsed = rawBody ? JSON.parse(rawBody) : null;
          if (parsed) {
            message = parsed.error || parsed.message || message;
          }
        } catch {
          // ignore JSON parse errors
        }

        if (res.status === 400 && message.includes('Cannot delete truck with existing sessions')) {
          alert('Cannot delete this truck while sessions are scheduled on it. Please reassign or cancel those sessions first.');
        } else {
          alert(`Failed to delete truck: ${message}`);
        }
      }
    } catch (error) {
      alert(`Failed to delete truck: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const activeTruck = findActiveTruck();

  return (
    <div className="flex flex-wrap items-center gap-2">
      {variant === 'toolbar' && <button
        type="button"
        onClick={() => openEditor('add')}
        disabled={busy}
        className="px-2 py-1 text-xs bg-white border border-slate-200 rounded-lg font-medium hover:bg-slate-100 disabled:opacity-50 sm:px-3 sm:text-sm"
      >
        ➕ <span className="sm:hidden">Add</span><span className="hidden sm:inline">Add Truck</span>
      </button>}
      {variant === 'menu' && <div className="relative">
        <button type="button" onClick={() => setMenuOpen(open => !open)} disabled={busy || !activeTruck} className="grid h-8 w-8 place-items-center rounded-md text-xl font-bold leading-none text-slate-500 hover:bg-slate-100 disabled:opacity-50" aria-label={`Manage ${activeTruck?.displayName || activeTruck?.name || 'truck'}`} aria-expanded={menuOpen}>⋮</button>
        {menuOpen && <div className="absolute right-0 top-9 z-30 w-40 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"><button type="button" onClick={() => { setMenuOpen(false); openEditor('edit'); }} className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50">🛠️ Modify Truck</button><button type="button" onClick={() => { setMenuOpen(false); deleteTruck(); }} className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-red-700 hover:bg-red-50">🗑️ Delete Truck</button></div>}
      </div>}
      {variant === 'legacy' && <>
      <button
        type="button"
        onClick={() => openEditor('edit')}
        disabled={busy || !activeTruck}
        className="px-2 py-1 text-xs bg-white border border-slate-200 rounded-lg font-medium hover:bg-slate-100 disabled:opacity-50 sm:px-3 sm:text-sm"
      >
        🛠️ <span className="sm:hidden">Edit</span><span className="hidden sm:inline">Modify Truck</span>
      </button>
      <button
        type="button"
        onClick={deleteTruck}
        disabled={busy || !activeTruck}
        className="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg font-medium hover:bg-red-100 disabled:opacity-50 sm:px-3 sm:text-sm"
      >
        🗑️ <span className="sm:hidden">Delete</span><span className="hidden sm:inline">Delete Truck</span>
      </button>
      </>}

      {editorState.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
          <form
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl space-y-4"
            onSubmit={submitEditor}
          >
            <h3 className="text-lg font-semibold text-slate-900">
              {editorState.mode === 'add' ? 'Add Training Truck' : 'Modify Training Truck'}
            </h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Truck name</label>
              <input
                type="text"
                value={editorState.name}
                onChange={(event) => setEditorState(prev => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter truck name"
                autoFocus
              />
            </div>
            <div>
              <span className="block text-sm font-medium text-slate-700 mb-1">Training type</span>
              <div className="flex gap-2">
                {['SKILL', 'ROAD'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setEditorState(prev => ({ ...prev, type: option }))}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${editorState.type === option
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                      }`}
                  >
                    {option === 'SKILL' ? 'Skills' : 'Road'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeEditor}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
